import React, { useCallback, useEffect, useState } from 'react';
import { getFeedback, listFeedback, updateFeedback } from './api';
import { stripHtmlToText } from './htmlText';
import { DetailOverlay } from './FeedbackDetailOverlay';
import { FeedbackDetail, FeedbackListItem } from './types';

// ---------- 리스트 뷰 (챗봇 "목록" 탭) ----------
// 2026-09-20 대개편: 6개 상태 + close_reason 별도 컬럼 + deleted_at soft-delete 분리.
// 카드형 여러 줄 배치 — 좁은 패널(모바일)·넓은 화면(PC) 모두 줄바꿈으로 대응, 한 줄 압축 금지.

const STATUS_META: Record<string, { label: string; color: string }> = {
  open:        { label: 'Open',            color: '#1565c0' },
  in_progress: { label: 'In Progress',     color: '#e94d20' },
  in_review:   { label: '확인요청',        color: '#7c3aed' },
  rework:      { label: '다시확인 (반려)', color: '#b91c1c' },
  reopen:      { label: '재확인 (Reopen)', color: '#d97706' },
  closed:      { label: 'Closed',          color: '#374151' },
};

/** 조회는 누구나, 상태 변경·삭제는 관리자만 — 401/403 은 원문 대신 안내문 */
const ADMIN_ONLY_MSG = '관리자만 처리할 수 있습니다.';
const errorMessage = (e: unknown, fallback: string): string => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const status = (e as any)?.response?.status;
  if (status === 401 || status === 403) return ADMIN_ONLY_MSG;
  return e instanceof Error ? e.message : fallback;
};

const CATEGORY_LABELS: Record<string, { label: string; color: string }> = {
  operation:   { label: '운영', color: '#1e40af' },
  development: { label: '개발', color: '#166534' },
  other:       { label: '기타', color: '#616161' },
  confirm:     { label: '확인요청', color: '#C2410C' },
  log_error:   { label: '로그·오류', color: '#b91c1c' },
};

const pill: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  borderRadius: 999,
  fontWeight: 700,
  lineHeight: 1.4,
};

const actionBtn: React.CSSProperties = {
  padding: '5px 12px',
  borderRadius: 6,
  fontSize: 12,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

/** category 를 주면 그 구분만 (예: 확인요청 탭 = confirm, 앱 구분 없이 전체). */
export const ListView: React.FC<{ appCode: string; category?: string }> = ({ appCode, category }) => {
  const [items, setItems] = useState<FeedbackListItem[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<FeedbackDetail | null>(null);
  const [selectedLoading, setSelectedLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await listFeedback({ appCode: category ? undefined : appCode, category, status: statusFilter || undefined, size: 30 });
      setItems(res.content);
    } catch (e) {
      setErr(errorMessage(e, '조회 실패'));
    } finally {
      setLoading(false);
    }
  }, [appCode, category, statusFilter]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const openDetail = async (id: number) => {
    setSelectedLoading(true);
    try {
      setSelected(await getFeedback(id));
    } catch (e) {
      setErr(errorMessage(e, '상세 조회 실패'));
    } finally {
      setSelectedLoading(false);
    }
  };

  const softDelete = async (id: number) => {
    if (!window.confirm('이 피드백을 삭제하시겠어요? (소프트 삭제)')) return;
    try {
      await updateFeedback(id, { softDelete: true });
      await refresh();
      if (selected?.id === id) setSelected(null);
    } catch (e) {
      setErr(errorMessage(e, '삭제 실패'));
    }
  };

  const setStatus = async (id: number, status: string) => {
    try {
      await updateFeedback(id, { status });
      await refresh();
      if (selected?.id === id) setSelected(await getFeedback(id));
    } catch (e) {
      setErr(errorMessage(e, '수정 실패'));
    }
  };

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: 'white' }}>
      {/* ui: dev-feedback-list_header — 제목 · 새로고침 · 상태 필터 */}
      <div id="dev-feedback-list_header" style={{ padding: '10px 12px', borderBottom: '1px solid #eee', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: '#444', fontWeight: 600 }}>이 앱 (`{appCode}`) 최근 피드백</span>
          <div style={{ flex: 1 }} />
          <button
            id="dev-feedback-list_btn-refresh"
            onClick={() => void refresh()}
            style={{ ...actionBtn, background: '#fafafa', border: '1px solid #ddd' }}
          >
            {loading ? '…' : '↻ 새로고침'}
          </button>
        </div>
        <div id="dev-feedback-list_filter-status" role="radiogroup" aria-label="상태 필터" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <FilterChip label="전체" color="#111827" active={statusFilter === ''} onClick={() => setStatusFilter('')} />
          {Object.entries(STATUS_META).map(([k, m]) => (
            <FilterChip key={k} label={m.label} color={m.color} active={statusFilter === k} onClick={() => setStatusFilter(k)} />
          ))}
        </div>
      </div>

      {err && <div style={{ color: '#c62828', fontSize: 12, padding: '8px 12px' }}>{err}</div>}

      <div style={{ flex: 1, overflowY: 'auto', background: '#f7f7f8', padding: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.length === 0 && !loading && !err && (
          <div style={{ padding: 24, textAlign: 'center', color: '#888', fontSize: 12 }}>
            {statusFilter ? `'${STATUS_META[statusFilter]?.label ?? statusFilter}' 상태의 피드백이 없습니다.` : '제출된 피드백이 없습니다.'}
          </div>
        )}
        {items.map((it) => (
          <FeedbackCard
            key={it.id}
            item={it}
            onOpen={() => void openDetail(it.id)}
            onStatus={(s) => void setStatus(it.id, s)}
            onDelete={() => void softDelete(it.id)}
          />
        ))}
      </div>

      {(selected || selectedLoading) && (
        <DetailOverlay loading={selectedLoading} detail={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
};

// ui: dev-feedback-list_card-{id} — 1행 번호·상태(바로 변경)·구분·일시 / 2행 화면 / 3행 요약 / 4행 작성자·동작
const FeedbackCard: React.FC<{
  item: FeedbackListItem;
  onOpen: () => void;
  onStatus: (status: string) => void;
  onDelete: () => void;
}> = ({ item, onOpen, onStatus, onDelete }) => (
  <div
    id={`dev-feedback-list_card-${item.id}`}
    style={{ background: 'white', border: '1px solid #ececec', borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 12, color: '#999', whiteSpace: 'nowrap' }}>#{item.id}</span>
      <StatusSelect id={`dev-feedback-list_select-status-${item.id}`} status={item.status} onChange={onStatus} />
      <CategoryBadge category={item.category} />
      <div style={{ flex: 1 }} />
      <span style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}>{new Date(item.createdAt).toLocaleString()}</span>
    </div>
    <div style={{ fontSize: 13, fontWeight: 600, color: '#222', wordBreak: 'keep-all', overflowWrap: 'anywhere' }}>
      {item.pageTitle || '(no page)'}
    </div>
    {item.summary && (
      <div
        style={{
          fontSize: 12,
          color: '#444',
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {stripHtmlToText(item.summary)}
      </div>
    )}
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 11, color: '#999' }}>
        {item.reporter || '(anon)'} · {item.env}
      </span>
      <div style={{ flex: 1 }} />
      <button id={`dev-feedback-list_btn-open-${item.id}`} onClick={onOpen} style={{ ...actionBtn, background: '#fafafa', border: '1px solid #ddd' }}>
        보기
      </button>
      <button
        id={`dev-feedback-list_btn-delete-${item.id}`}
        onClick={onDelete}
        title="소프트 삭제 (deleted_at)"
        style={{ ...actionBtn, background: '#fff5f5', border: '1px solid #f5b5b5', color: '#c62828' }}
      >
        삭제
      </button>
    </div>
  </div>
);

/** 현재 상태 뱃지 = 변경 select (별도 입력칸 없이 뱃지를 눌러 바로 변경) */
const StatusSelect: React.FC<{ id: string; status: string; onChange: (s: string) => void }> = ({ id, status, onChange }) => {
  const meta = STATUS_META[status] || { label: status, color: '#666' };
  return (
    <span style={{ ...pill, position: 'relative', background: meta.color }}>
      <select
        id={id}
        aria-label="상태 변경"
        title="눌러서 상태 변경"
        value={status}
        onChange={(e) => onChange(e.target.value)}
        style={{
          appearance: 'none',
          WebkitAppearance: 'none',
          background: 'transparent',
          border: 'none',
          color: 'white',
          fontSize: 11,
          fontWeight: 700,
          padding: '3px 22px 3px 10px',
          borderRadius: 999,
          cursor: 'pointer',
          outline: 'none',
        }}
      >
        {!STATUS_META[status] && <option value={status} style={{ color: '#222', background: 'white' }}>{status}</option>}
        {Object.entries(STATUS_META).map(([k, m]) => (
          <option key={k} value={k} style={{ color: '#222', background: 'white' }}>
            {m.label}
          </option>
        ))}
      </select>
      <span aria-hidden style={{ position: 'absolute', right: 8, color: 'white', fontSize: 9, pointerEvents: 'none' }}>▼</span>
    </span>
  );
};

const CategoryBadge: React.FC<{ category?: string }> = ({ category }) => {
  const meta = CATEGORY_LABELS[category || 'other'] || CATEGORY_LABELS.other;
  return (
    <span style={{ ...pill, fontSize: 11, padding: '2px 8px', background: meta.color, color: 'white' }}>
      {meta.label}
    </span>
  );
};

const FilterChip: React.FC<{ label: string; color: string; active: boolean; onClick: () => void }> = ({ label, color, active, onClick }) => (
  <button
    type="button"
    role="radio"
    aria-checked={active}
    onClick={onClick}
    style={{
      ...pill,
      fontSize: 11,
      padding: '4px 10px',
      cursor: 'pointer',
      border: `1px solid ${active ? color : '#ddd'}`,
      background: active ? color : 'white',
      color: active ? 'white' : '#555',
      fontWeight: active ? 700 : 500,
    }}
  >
    {label}
  </button>
);
